import { useEffect, useState } from 'react';

import { useLocation } from '@remix-run/react';

import type { CommentType } from '~/interfaces/comment';

import { useNonRevalidatingFetcher } from '../useNonRevalidatingFetcher';

export type OrderType = 'asc' | 'desc' | 'popular';

interface Props {
  postId: string;
  commentId?: string;
  order?: OrderType;
  enabled?: boolean;
}

type PageToAdd = {
  page: number;
  added: boolean;
};

interface ReturnProps {
  initialFetched: boolean;
  comments: any[];
  optimisticallyRenderNewComment: (newComment: CommentType, parentCommentId?: string) => void;
  optimisticallyDeleteTopLevelComment: (commentId: string) => void;
  optimisticallyDeleteSubComment: (commentId: string) => void;
  optimisticallyUpdateComment: (commentId: string, newContent: string) => void;
  itemsRemaining: number;
  isLoadingMore: boolean;
  remainingCount: number;
  hasNextPage: boolean;
  loadMore: () => void;
  commentOrder: OrderType | undefined;
  reorderComments: (order: OrderType) => void;
}

const insertSubComment = (comments: CommentType[], newComment: CommentType, parentCommentId: string): CommentType[] => {
  return comments.map((comment: CommentType) => {
    if (comment.id === parentCommentId) {
      return {
        ...comment,
        sub_comments: Array.isArray(comment.sub_comments)
          ? [{ ...newComment }, ...comment.sub_comments]
          : [{ ...newComment }],
      };
    }

    comment.sub_comments = insertSubComment(comment.sub_comments || [], newComment, parentCommentId);

    return comment;
  });
};

const deleteSubComment = (comments: CommentType[], commentId: string): CommentType[] => {
  return comments.map((comment: CommentType) => {
    if (comment.id === commentId) {
      return {
        ...comment,
        deleted_by_commenter_at: new Date().toISOString(),
      };
    }

    comment.sub_comments = deleteSubComment(comment.sub_comments || [], commentId);

    return comment;
  });
};

const updateCommentContent = (comments: CommentType[], commentId: string, newContent: string): CommentType[] => {
  return comments.map((comment: CommentType) => {
    if (comment.id === commentId) {
      return {
        ...comment,
        content: newContent,
        updated_at: new Date().toISOString(),
      };
    }

    comment.sub_comments = updateCommentContent(comment.sub_comments || [], commentId, newContent);

    return comment;
  });
};

type FetcherData = {
  comments: CommentType[];
  pagination: {
    page: number;
    total: number;
    per_page: number;
  };
  items_remaining?: number;
};
const usePaginatedComments = ({ postId, commentId, order, enabled = true }: Props): ReturnProps => {
  const [commentOrder, setCommentOrder] = useState<OrderType | undefined>(order);
  const location = useLocation();
  const fetcher = useNonRevalidatingFetcher<() => Promise<FetcherData>>();
  const [initialFetched, setInitialFetched] = useState(false);
  const [comments, setComments] = useState<CommentType[]>([]);
  const [pageToAdd, setPageToAdd] = useState<PageToAdd>({
    page: 1,
    added: false,
  });

  const page = fetcher.data?.pagination?.page || 1;
  const total = fetcher.data?.pagination.total || 0;
  const perPage = fetcher.data?.pagination.per_page || 10;
  const current = page * perPage;
  const nextPage = page + 1;
  const hasNextPage = current < total;

  const refreshState = () => {
    setComments([]);
    setInitialFetched(false);
    setPageToAdd({
      page: 1,
      added: false,
    });
  };

  const reorderComments = (order: OrderType) => {
    setCommentOrder(order);
    refreshState();
  };

  // Reset comments state on page transition
  useEffect(() => {
    setComments([]);
  }, [location.pathname]);

  // Set Initial Posts
  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (pageToAdd.page === 1 && !pageToAdd.added) {
      const params = new URLSearchParams();
      params.append('post_id', postId);
      if (commentId) params.append('comment_id', commentId);
      if (commentOrder) params.append('order', commentOrder);

      fetcher.load(`/paginated_comments?${params.toString()}`);
    }
  }, [location, pageToAdd, enabled]);

  useEffect(() => {
    if (fetcher.data) {
      if (pageToAdd.page === fetcher.data.pagination.page && !pageToAdd.added) {
        setComments([...comments, ...fetcher.data.comments]);
        setPageToAdd({
          page: pageToAdd.page,
          added: true,
        });
      }

      if (!initialFetched) setInitialFetched(true);
    }
  }, [fetcher.data]);

  return {
    initialFetched,
    comments,
    optimisticallyRenderNewComment: (newComment: CommentType, parentCommentId?: string) => {
      if (parentCommentId) {
        setComments(insertSubComment(comments, newComment, parentCommentId));
      } else {
        setComments([newComment, ...comments]);
      }
    },
    optimisticallyDeleteTopLevelComment: (commentId: string) => {
      const comment = comments.find((c: CommentType) => c.id === commentId);
      if (!comment) return;
      const optimisticallyDeletedComment = {
        ...comment,
        deleted_by_commenter_at: new Date().toISOString(),
      };

      // Replace the comment with the optimistically deleted comment
      setComments(comments.map((c: CommentType) => (c.id === commentId ? optimisticallyDeletedComment : c)));
    },
    optimisticallyDeleteSubComment: (commentId: string) => {
      setComments(deleteSubComment(comments, commentId));
    },
    optimisticallyUpdateComment: (commentId: string, newContent: string) => {
      setComments(updateCommentContent(comments, commentId, newContent));
    },
    reorderComments,
    commentOrder,
    itemsRemaining: fetcher.data?.items_remaining ?? 0,
    isLoadingMore: initialFetched && fetcher.state === 'loading',
    remainingCount: total - current,
    hasNextPage,
    loadMore: () => {
      const params = new URLSearchParams();
      params.append('post_id', postId);
      params.append('page', nextPage.toString());
      if (commentId) params.append('comment_id', commentId);
      if (order) params.append('order', order);
      setPageToAdd({
        page: nextPage,
        added: false,
      });

      fetcher.load(`/paginated_comments?${params.toString()}`);
    },
  };
};

export default usePaginatedComments;
